import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STALE_FIVE_MINUTES, STALE_ONE_HOUR } from 'api/types';
import Cookies from 'js-cookie';
import { CookieKeyEnum } from 'utils/data/enums';
import {
  fetchAuth,
  fetchUsersContactInfo,
  putUsersContactInfo,
  putUsersPassword,
} from 'api/requests/requests';
import { StringOrNumber } from 'utils/data/data-types';
import { ContactInfoSchemaType, isOperatorRole } from 'remote/global';
import { AxiosError } from 'axios';
import { PasswordSchemaType } from 'components/forms/AccountFactory/types';
import { useResetAppState } from '@/setup/SessionProvider';

export const usersQueryKeys = {
  all: ['users'] as const,
  currentAccesses: () => [...usersQueryKeys.all, 'current', 'accesses'] as const,
  detail: (userId: StringOrNumber) => [...usersQueryKeys.all, userId] as const,
  contactInfo: (userId: StringOrNumber) =>
    [...usersQueryKeys.detail(userId), 'contactInfo'] as const,
  password: (userId: StringOrNumber) => [...usersQueryKeys.detail(userId), 'password'] as const,
};

export function useUsersCurrentAccessesQuery() {
  const refreshToken = Cookies.get(CookieKeyEnum.REFRESH_TOKEN);
  const resetState = useResetAppState();

  const query = useQuery({
    queryKey: usersQueryKeys.currentAccesses(),
    queryFn: fetchAuth,
    enabled: refreshToken != null,
    staleTime: STALE_FIVE_MINUTES,
    select: ({ data }) => data,
  });

  // Log out the user if they attempt to log in with an invalid role
  if (query.isSuccess) {
    const [role] = query.data.roles;
    if (!isOperatorRole(role)) resetState();
  }

  return query;
}

export function useOperatorId() {
  const { data: userInfo } = useUsersCurrentAccessesQuery();

  // Let's assume operatorId is always available with !
  return userInfo?.operatorId!;
}

export function useUserId() {
  const { data: { userId } = {} } = useUsersCurrentAccessesQuery();

  // Let's assume operatorId is always available with !
  return userId!;
}

export function useUsersContactInfoQuery() {
  const userId = useUserId();

  return useQuery({
    queryKey: usersQueryKeys.contactInfo(userId),
    queryFn: () => fetchUsersContactInfo(String(userId)),
    enabled: userId != null,
    staleTime: STALE_ONE_HOUR,
    select: ({ data }) => data,
  });
}

export function useUsersContactInfoMutation() {
  const queryClient = useQueryClient();
  const userId = useUserId();

  return useMutation<ContactInfoSchemaType, AxiosError, ContactInfoSchemaType>({
    mutationKey: usersQueryKeys.contactInfo(userId),
    mutationFn: (v) => putUsersContactInfo(String(userId), v).then((d) => d.data),
    onSuccess: async (data) => {
      queryClient.setQueryData(usersQueryKeys.contactInfo(userId!), { data });
    },
  });
}

export function useUsersPasswordMutation() {
  const userId = useUserId();

  return useMutation<number, AxiosError, PasswordSchemaType>({
    mutationKey: usersQueryKeys.password(userId),
    mutationFn: (v) => putUsersPassword(String(userId), v).then((d) => d.status),
  });
}
